<template>
  <DialogHeader>
    <template #right>
      <div class="cursor-pointer" @click="close()">Close</div>
    </template>
    Cancel subscription
  </DialogHeader>
  <DialogContent>
    <div class="mb-[18px]">
      <div class="mb-[14px]">
        Hey {{ userStore.user?.name }}, we're sorry you're thinking of
        cancelling.
      </div>
      <div>
        By canceling your subscription you will have
        <span class="font-semibold font-primary -mr-1">
          access to your copyrighted work until {{ subscriptionRenewalDate }}
        </span>
        . Once your account is canceled the following will happen:
      </div>
    </div>
    <div
      class="flex flex-col gap-y-[14px] bg-gray-200/30 px-[22px] py-6 rounded-md"
    >
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="text-gray-600 -mt-px">
          After this date, you will no longer be able to access your copyrighted
          work unless you resubscribe to a plan.
        </div>
      </div>
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="text-gray-600 -mt-px">
          Upon canceling you cannot add new files for copyright protection
          beyond your current plan limits or if it increases your Dacr balance.
        </div>
      </div>
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="text-gray-600 -mt-px">
          When your billing period ends, your account balance will continue to
          accrue and this accrued balance will need to be paid in full if you
          want to resubscribe again.
        </div>
      </div>
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="text-gray-600 -mt-px">
          Any pending sales, transfers, or licenses will be immediately
          canceled.
        </div>
      </div>
    </div>

    <div v-if="hasOutsandingBalance" class="mt-4">
      To continue to cancel your subscription you must pay
      <span class="font-semibold font-primary -mr-1">
        your outstanding balance of
        {{ outstandingBalance }}
      </span>
      .
    </div>
    <div class="mt-6">
      <UiButton class="w-full!" danger @click="handleClick">
        {{ buttonCopy }}
      </UiButton>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Icon, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'

import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { formatPrice } from '@/shared/utils/money'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useUserStore } from '@/shared/stores/user'
import { useBillingStore } from '@/features/billing/store'

const { close, open } = useDialog()

const userStore = useUserStore()
const billingStore = useBillingStore()

const user = computed(() => userStore.user)

const subscriptionRenewalDate = computed(() =>
  formatTimestamp(billingStore.billing?.plan.dateInfo?.date, {
    formatString: user.value?.dateFormat,
  })
)

const hasOutsandingBalance = computed(() =>
  billingStore.billing?.upcomingDetails.upcomingBilling?.breakdown.some(
    (breakdown) => breakdown.outstanding
  )
)

const outstandingBalance = computed(() => {
  const totalOutstanding =
    billingStore.billing?.upcomingDetails.upcomingBilling?.breakdown.reduce(
      (acc, breakdown) =>
        (acc += breakdown.outstanding ? breakdown.amount || 0 : 0),
      0
    ) || 0

  return formatPrice({
    amount: totalOutstanding / 100,
    currency: billingStore.billing?.upcomingDetails.upcomingBilling?.currency,
  })
})

const buttonCopy = hasOutsandingBalance.value
  ? 'Pay and cancel subscription'
  : 'Cancel subscription'

const handleClick = async () => {
  open(Dialogs.ConfirmCancelSubscription)
}
</script>
